import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

export const eventsKeys = {
  all: (fleetId: number, vehicleId: number, page: number, per_page: number) =>
    [
      "events",
      "fleetId",
      fleetId,
      "vehicleId", 
      vehicleId,
      "page",
      page,
      "per_page",
      per_page,
    ] as const,
  allEventsWithMileage: (fleetId: number, vehicleId: number) =>
    ["events", "fleetId", fleetId, "vehicleId", vehicleId, "allWithMileage"] as const,
  // Add a base key for invalidating all events for a vehicle
  byVehicle: (fleetId: number, vehicleId: number) =>
    ["events", "fleetId", fleetId, "vehicleId", vehicleId] as const,
};

interface TEvent {
  id: number;
  name: string;
  datetime: string;
  created_at: string;
  updated_at: string;
  mileage: string | null;
  event_type: {
    id: number;
    libelle: string;
  };
  vehicle: {
    id: number;
    number: string;
  };
  address?: {
    id: number;
    raw: string;
    lat?: number;
    lgt?: number;
  };
}

interface ResponseGetEvents {
  data: TEvent[];
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

interface UseGetAllEventsOptions {
  enabled?: boolean;
  staleTime?: number;
}

export const useGetAllEvents = (
  fleetId: number,
  vehicleId: number,
  page: number = 1,
  per_page: number = 10,
  options: UseGetAllEventsOptions = {}
) => {
  const { enabled = true, staleTime = 0 } = options;
  const { data, error, isLoading, isFetching, ...res } = useQuery({
    queryKey: eventsKeys.all(fleetId, vehicleId, page, per_page),
    queryFn: async (): Promise<ResponseGetEvents> => {
      const response = await axios.get(
        `/fleets/${fleetId}/vehicles/${vehicleId}/events`,
        {
          params: { page, per_page }
        }
      );
      return response.data;
    },
    // FIX: stricter guards — fleetId must also be > 0
    enabled: enabled && 
             typeof fleetId === 'number' && !isNaN(fleetId) && fleetId > 0 &&
             typeof vehicleId === 'number' && !isNaN(vehicleId) && vehicleId > 0,
    staleTime,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
  return {
    events: data?.data ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.current_page ?? page,
    perPage: data?.per_page ?? per_page,
    lastPage: data?.last_page ?? 1,
    isLoading,
    isFetching,
    error,
    ...res,
  };
};

/**
 * Event with mileage information for validation purposes
 */
export interface EventWithMileage {
  id: number;
  datetime: string;
  mileage: number;
  name: string;
}

/**
 * Hook to fetch all events with mileage for chronological validation
 * This fetches all pages to get complete mileage history
 */
export const useGetAllEventsWithMileage = (fleetId: number, vehicleId: number) => {
  const { data, error, isLoading, ...res } = useQuery({
    queryKey: eventsKeys.allEventsWithMileage(fleetId, vehicleId),
    queryFn: async (): Promise<EventWithMileage[]> => {
      const allEvents: EventWithMileage[] = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await axios.get<ResponseGetEvents>(
          `/fleets/${fleetId}/vehicles/${vehicleId}/events`,
          {
            params: { page: currentPage, per_page: 100 }
          }
        );

        const events = response.data.data;
        
        // Extract events with valid mileage
        events.forEach(event => {
          if (event.mileage && event.datetime) {
            const mileage = parseInt(event.mileage, 10);
            if (!isNaN(mileage) && mileage > 0) {
              allEvents.push({
                id: event.id,
                datetime: event.datetime,
                mileage,
                name: event.name
              });
            }
          }
        });

        // Check if there are more pages
        hasMore = currentPage < response.data.last_page;
        currentPage++;
      }

      // Sort by datetime ascending (oldest first)
      return allEvents.sort((a, b) => 
        new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
      );
    },
    // FIX: stricter guards — fleetId must also be > 0
    enabled: typeof fleetId === 'number' && !isNaN(fleetId) && fleetId > 0 &&
             typeof vehicleId === 'number' && !isNaN(vehicleId) && vehicleId > 0,
    staleTime: 0,
  });

  return {
    eventsWithMileage: data ?? [],
    isLoading,
    error,
    ...res,
  };
};

interface AddEventData {
  name: string;
  event_type_id: number;
  vehicle_id: number;
  note?: string;
  datetime?: string;
  mileage?: string;
  address?: {
    raw: string;
    place_id?: string;
  };
  documents?: Array<{
    url: string;
    doc_name: string;
    doc_type: string;
  }>;
}

export const useAddEvent = (fleetId?: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (eventData: AddEventData): Promise<TEvent> => {
      const response = await axios.post('/event/add', eventData);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.vehicle?.id && fleetId) {
        queryClient.invalidateQueries({
          queryKey: eventsKeys.byVehicle(fleetId, data.vehicle.id),
          refetchType: 'active',
        });
        queryClient.invalidateQueries({
          queryKey: eventsKeys.allEventsWithMileage(fleetId, data.vehicle.id),
          refetchType: 'active',
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ['events'],
          refetchType: 'active',
        });
      }
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || t("common.error", "An error occurred")
      );
    },
  });
};

export interface EventType {
  id: number;
  libelle: string;
}

export const eventTypesKeys = {
  enabled: () => ['event-types', 'enabled'] as const,
};

export const useGetEnabledEventTypes = () => {
  const { data, error, isLoading, ...res } = useQuery({
    queryKey: eventTypesKeys.enabled(),
    queryFn: async (): Promise<EventType[]> => {
      const response = await axios.get('/event/type/list/enabled');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  return {
    eventTypes: data ?? [],
    isLoading,
    error,
    ...res,
  };
};
